"use client";

import { usePathname, useSearchParams } from "next/navigation";
import Script from "next/script";
import { useEffect, useRef } from "react";

import { env } from "./env";

import type { AnalyticsProviderClientStrategy } from "@workspace/analytics";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GoogleAnalyticsPageView = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!pathname || typeof window === "undefined" || !window.gtag) {
      return;
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const search = searchParams.toString();
    const pagePath = search ? `${pathname}?${search}` : pathname;

    window.gtag("event", "page_view", {
      page_path: pagePath,
      page_location: `${window.location.origin}${pagePath}`,
      page_title: document.title,
    });
  }, [pathname, searchParams]);

  return null;
};

export const GoogleAnalyticsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const measurementId = env.NEXT_PUBLIC_GOOGLE_ANALYTICS_MEASUREMENT_ID;

  return (
    <>
      {children}
      <GoogleAnalyticsPageView />
      <Script
        id="google-analytics"
        strategy="afterInteractive"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
      />
      <Script
        id="google-analytics-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            window.gtag = gtag;
            gtag('js', new Date());
            gtag('config', '${measurementId}');
          `,
        }}
      />
    </>
  );
};

export const strategy = {
  Provider: GoogleAnalyticsProvider,
  track: (event, data) => {
    if (typeof window === "undefined" || !window.gtag) {
      return;
    }

    window.gtag("event", event, data);
  },
  identify: (userId, traits) => {
    if (typeof window === "undefined" || !window.gtag) {
      return;
    }

    window.gtag("config", env.NEXT_PUBLIC_GOOGLE_ANALYTICS_MEASUREMENT_ID, {
      user_id: userId,
      ...traits,
    });
  },
  reset: () => {
    if (typeof window === "undefined" || !window.gtag) {
      return;
    }

    window.gtag("config", env.NEXT_PUBLIC_GOOGLE_ANALYTICS_MEASUREMENT_ID, {
      user_id: null,
    });
  },
} satisfies AnalyticsProviderClientStrategy;
